import { Fragment, lazy, Suspense, useState } from 'react';

import { Modal, Notice } from '@/components/Modal';
import { dismissPoll, markVoted, useCastVote, type PollView } from '@/lib/polls/usePolls';
import { isHomepagePreviewKey, type HomepagePreviewKey } from '@/pages/docs/lab/homepage/previewKeys';

/**
 * 투표 한 건을 묻는 창 — 로그인해서 홈에 들어온 사람 앞에 선다(사용자 지시 2026-09-02).
 *
 * <h2>결과가 없다</h2>
 * 던지고 나면 창이 닫히고 끝이다. 집계는 마감 뒤에만 열리므로 여기서 보여 줄 것이 없고,
 * <b>없는 것을 자리로 만들지 않는다</b>. 마감 뒤의 결과는 홈이 한 번 더 띄운다.
 *
 * <h2>「직접 적기」 는 선택지 하나로 선다</h2>
 * 자유칸을 켠 투표에서는 마지막 줄이 <b>고를 수 있는 칸</b>이다(V210). 목록 바깥에 입력칸만
 * 따로 두면 「골랐는데 적기도 했다」 가 되고, 그때 한 표를 어디로 셀지 정할 방법이 없다.
 *
 * <h2>「나중에」 는 이번 판만이다</h2>
 * 미룬 것은 서버에 남기지 않는다({@link dismissPoll}) — 누가 미뤘는지가 쌓이면 안 한 사람이
 * 지목되고, 비밀투표에서 그것은 하지 않기로 한 일에 가깝다. 다시 로그인하면 다시 묻고,
 * 그 사이에는 TO DO 할일 줄이 자리를 지킨다.
 *
 * <h2>눌러 보고 고르는 선택지 (V350 · 사용자 요청 2026-09-29)</h2>
 * 선택지에 {@code previewKey} 가 붙어 있으면 <b>그 줄을 누르는 것이 곧 «열어 보기»</b> 다 —
 * 홈페이지 시안이 전체 화면으로 서고, 실험실에서처럼 메뉴를 눌러 속 페이지까지 둘러볼 수 있다.
 * 글자만 읽고 고른 표는 이름에 대한 표이지 홈페이지에 대한 표가 아니다.
 *
 * <p>처음에는 줄 오른쪽에 「눌러보기」 단추만 달고 줄 자체는 <b>고르기</b> 로 뒀는데, 사용자가
 * 두 번 「A~E 를 눌러도 안 열린다」 고 했다(2026-09-29). 요청은 처음부터 「시안을 눌렀을 때
 * 전체화면」 이었다 — 눌러 보라고 만든 줄에서 가장 큰 과녁이 다른 일을 하고 있었다.
 *
 * <p>그래서 이 줄만 {@code <label>} 을 쓰지 않는다. 껍데기가 label 이면 어디를 눌러도 동그라미가
 * 켜져, 여는 일과 고르는 일이 같은 과녁을 다툰다. <b>동그라미는 고르고, 나머지는 연다.</b>
 *
 * <p>고르는 일과 던지는 일은 <b>여전히 갈라져 있다</b>. 전체 화면의 「이 시안 고르기」 는
 * 동그라미만 옮기고 창을 닫는다 — 둘러보다 누르는 자리에서 표까지 들어가면 되돌릴 길이 없다.
 *
 * <p>시안은 <b>누른 사람에게만</b> 내려온다({@code lazy}). 홈에 들어오는 모든 사람이 시안 다섯
 * 벌을 함께 받을 이유가 없어, 미리 오는 것은 열쇠 다섯 줄뿐이다({@code previewKeys}).
 */
const WRITE_IN = 'write-in';

const HomepagePreview = lazy(() =>
  import('@/pages/docs/lab/homepage/HomepagePreview').then((module) => ({ default: module.HomepagePreview })),
);

export function PollModal({ poll, onClose }: { poll: PollView; onClose: () => void }) {
  const [choice, setChoice] = useState<string | null>(null);
  const [idea, setIdea] = useState('');
  /* 지금 전체 화면으로 펴 둔 선택지. 열쇠만이 아니라 <b>어느 줄인지</b>까지 들고 있어야
     「이 시안 고르기」 가 그 줄의 동그라미를 켤 수 있다. */
  const [previewing, setPreviewing] = useState<{ optionId: string; key: HomepagePreviewKey; caption: string } | null>(
    null,
  );
  /* 고른 칸에 덧붙이는 말(V353). 「직접 적기」 와 <b>다른 칸</b>에 들고 있다 — 한 칸에 담으면
     자유칸에 적다가 마음을 바꿔 선택지를 고른 사람의 글이 그 선택지에 따라붙는다. */
  const [note, setNote] = useState('');
  const cast = useCastVote();

  const writingIn = choice === WRITE_IN;
  const canSubmit = writingIn ? Boolean(idea.trim()) : Boolean(choice);

  /* 의견 칸을 연 선택지를 고르고 있나. 칸이 비어 있어도 투표는 된다 — 「다섯 다 아니다」 까지만
     분명한 사람의 표가 갈 곳이 있어야 한다(V353). */
  const noted = poll.options.find((option) => option.id === choice && option.noteLabel);

  const submit = () => {
    if (!canSubmit || cast.isPending) return;
    cast.mutate(
      writingIn
        ? { pollId: poll.id, writeIn: idea.trim() }
        : { pollId: poll.id, optionId: choice, writeIn: noted && note.trim() ? note.trim() : null },
      {
        onSuccess: () => {
          /*
            던진 것을 표시해 둔다 (사용자 지적 2026-09-02: "투표했는데 또 나온다").

            서버는 이미 안다 — 다시 물어 오지 않는다. 문제는 그 답이 오기까지의 <b>몇백
            밀리초</b>다: 창을 닫으면 화면은 아직 손에 든 옛 목록으로 다시 그려지고, 거기에는
            방금 던진 투표가 그대로 있어 창이 한 번 더 서거나 게시판에 「투표하기」 가 남는다.

            <b>미룬 것과 다른 이름으로</b> 넣는다({@link markVoted}) — 게시판은 던진 것만 보고
            버튼을 거둬야 하고, 미룬 사람에게는 그 버튼이 그대로 있어야 한다.
          */
          markVoted(poll.id);
          onClose();
        },
      },
    );
  };

  /* 「나중에」 로 닫는 것과 투표하고 닫는 것을 가른다 — 투표한 것은 서버가 이미 알고 있어
     다시 뜨지 않지만, 미룬 것은 이 표시가 없으면 홈이 다시 그려질 때마다 선다. */
  const later = () => {
    dismissPoll(poll.id);
    onClose();
  };

  return (
    <Modal
      title={poll.title}
      maxWidth="480px"
      onClose={later}
      closeDisabled={cast.isPending}
      footer={
        <>
          <button onClick={later} className="btn-secondary" disabled={cast.isPending}>
            나중에
          </button>
          <button onClick={submit} className="btn-primary" disabled={!canSubmit || cast.isPending}>
            {cast.isPending ? '보내는 중...' : '투표'}
          </button>
        </>
      }
    >
      {/*
        왜 묻는지(V211). 투표를 여는 사람이 적은 것 그대로 선다 — 화면이 지어내는 안내문이
        아니다. 안 적었으면 아무것도 서지 않는다.

        줄바꿈을 살린다(`whitespace-pre-wrap`). 여러 문단으로 적어 둔 사정이 한 덩어리로
        뭉치면, 읽으라고 적은 것을 읽지 않게 된다.
      */}
      {poll.description && (
        <p
          className="text-ui-sm"
          style={{ color: 'var(--text-secondary)', whiteSpace: 'pre-wrap' }}
        >
          {poll.description}
        </p>
      )}

      <div className="space-y-1.5">
        {poll.options.map((option) => {
          const previewKey = isHomepagePreviewKey(option.previewKey) ? option.previewKey : null;

          return (
            <Fragment key={option.id}>
            <ChoiceRow
              pollId={poll.id}
              value={option.id}
              checked={choice === option.id}
              disabled={cast.isPending}
              onSelect={setChoice}
              /* 눌러 볼 수 있는 줄에만 준다. 모르는 열쇠에는 주지 않는다 — 눌러도 아무것도
                 안 열리는 과녁을 두느니, 평범한 선택지로 서는 편이 낫다. */
              onOpen={
                previewKey
                  ? () => setPreviewing({ optionId: option.id, key: previewKey, caption: option.label })
                  : undefined
              }
            >
              <span className="text-ui-sm" style={{ color: 'var(--text-primary)' }}>
                {option.label}
              </span>
            </ChoiceRow>

            {/*
              고른 칸에 덧붙이는 말(V353). <b>고른 뒤에만</b> 선다 — 다섯 줄 아래에 늘 열려 있는
              칸이 있으면 「적어야 하는 것」 으로 읽히고, 적을 말이 없는 사람이 그 줄을 피한다.

              줄 <b>안</b>이 아니라 바로 아래에 둔다. 선택지 줄은 껍데기가 {@code <label>} 이거나
              통째로 단추라, 그 안에 칸을 넣으면 누를 때마다 줄이 반응한다.
            */}
            {option.noteLabel && choice === option.id && (
              <input
                className="input-field"
                style={{ marginLeft: 34, width: 'calc(100% - 34px)', padding: '6px 10px' }}
                value={note}
                maxLength={200}
                disabled={cast.isPending}
                placeholder={option.noteLabel}
                onChange={(event) => setNote(event.target.value)}
              />
            )}
            </Fragment>
          );
        })}

        {poll.allowWriteIn && (
          <ChoiceRow
            pollId={poll.id}
            value={WRITE_IN}
            checked={writingIn}
            disabled={cast.isPending}
            onSelect={setChoice}
          >
            {/*
              칸을 누르면 그 줄이 골라진다(onFocus). 적기 시작했는데 동그라미가 딴 데 있으면,
              적어 놓고도 그 표는 다른 칸으로 간다.
            */}
            <input
              className="input-field"
              style={{ padding: '4px 8px' }}
              value={idea}
              maxLength={200}
              disabled={cast.isPending}
              placeholder="직접 적기"
              onFocus={() => setChoice(WRITE_IN)}
              onChange={(event) => setIdea(event.target.value)}
            />
          </ChoiceRow>
        )}
      </div>

      {poll.closesAt && (
        <p className="text-ui-xs" style={{ color: 'var(--text-muted)' }}>
          {new Date(poll.closesAt).toLocaleString('ko-KR', {
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          })}{' '}
          마감
        </p>
      )}

      {/*
        누른 사람에게만 나온다. 「불러오는 중」 으로 덮는 자리가 아니다 — 읽기가 실패한 것이라면
        사유를 감추는 편이 맞지만, 이것은 <b>보냈는데 안 들어간 것</b>이라 다시 눌러야 할지를
        본인이 알아야 한다(이미 했다·마감됐다는 서버가 그대로 말해 준다).
      */}
      {cast.isError && <Notice tone="warning">{(cast.error as Error).message}</Notice>}

      {/*
        전체 화면으로 편 시안. 몸통에 직접 붙으므로(그쪽에서 portal 한다) 이 자리에 적혀 있어도
        모달 안에 갇히지 않는다 — 여기 있는 이유는 「어느 줄을 펴 두었나」 를 이 창이 알기 때문이다.

        받아 오는 동안은 <b>아무것도 세우지 않는다</b>. 「불러오는 중」 딱지가 깜빡였다가 시안이
        서면 판만 시끄러워지고, 기다리는 사람이 할 수 있는 일은 늘지 않는다.
      */}
      {previewing && (
        <Suspense fallback={null}>
          <HomepagePreview
            previewKey={previewing.key}
            caption={previewing.caption}
            picked={choice === previewing.optionId}
            onPick={() => {
              setChoice(previewing.optionId);
              setPreviewing(null);
            }}
            onClose={() => setPreviewing(null)}
          />
        </Suspense>
      )}
    </Modal>
  );
}

/**
 * 고를 수 있는 줄 하나. 준비된 선택지와 「직접 적기」 가 <b>같은 껍데기</b>를 쓴다 — 같은 무게로
 * 보여야 한다.
 *
 * @param onOpen 줄을 누르면 할 일. 주면 <b>껍데기가 {@code <label>} 이 아니게 된다</b>(V350) —
 *   label 은 어디를 눌러도 동그라미를 켜므로, 「눌러 보는 줄」 에서는 가장 큰 과녁이 여는 일이
 *   아니라 고르는 일에 쓰인다. 주지 않으면 지금까지대로 줄 전체가 고르기다.
 */
function ChoiceRow({
  pollId,
  value,
  checked,
  disabled,
  onSelect,
  onOpen,
  children,
}: {
  pollId: string;
  value: string;
  checked: boolean;
  disabled: boolean;
  onSelect: (value: string) => void;
  onOpen?: () => void;
  children: React.ReactNode;
}) {
  const frame = {
    borderColor: checked ? 'var(--accent)' : 'var(--border)',
    background: checked ? 'var(--accent-bg)' : 'transparent',
  };

  const radio = (
    <input
      type="radio"
      name={`poll-${pollId}`}
      checked={checked}
      onChange={() => onSelect(value)}
      disabled={disabled}
      className="flex-shrink-0"
      /* 여는 줄에서는 동그라미가 «고르는» 유일한 과녁이다. 이름표가 없으니 읽어 줄 말을 붙인다. */
      aria-label={onOpen ? '이 시안 고르기' : undefined}
    />
  );

  if (!onOpen) {
    return (
      <label
        className="flex cursor-pointer items-center gap-2.5 rounded-lg border px-3 py-2.5"
        style={frame}
      >
        {radio}
        <span className="min-w-0 flex-1">{children}</span>
      </label>
    );
  }

  /* 동그라미는 고르고, 나머지는 연다. 단추를 겹쳐 놓지 않고 <b>줄의 몸통 자체</b>가 단추다 —
     키보드로도 닿고(Tab·Enter), 「어디를 눌러야 하나」 를 묻지 않게 된다. */
  return (
    <div className="flex items-center gap-2.5 rounded-lg border px-3 py-2.5" style={frame}>
      {radio}
      <button
        type="button"
        className="flex min-w-0 flex-1 cursor-pointer items-center gap-2 text-left"
        style={{ background: 'transparent', border: 0, padding: 0 }}
        disabled={disabled}
        onClick={onOpen}
      >
        <span className="min-w-0 flex-1">{children}</span>
        {/* 이 줄이 «열린다» 는 표시. 누르는 자리는 줄 전체이므로 단추가 아니라 <b>안내</b>다. */}
        <span className="flex-shrink-0 text-ui-xs" style={{ color: 'var(--accent)' }}>
          눌러보기
        </span>
      </button>
    </div>
  );
}
